import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { calculatePermissions, needsModuleSelection, type Permissions, type UserPlan } from '../utils/permissions';

export function usePermissions() {
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsModule, setNeedsModule] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      console.warn('[usePermissions] Loading timeout exceeded, forcing completion');
      setLoading(false);
    }, 3000);

    loadPermissions().finally(() => clearTimeout(timeout));

    let channel: any = null;

    try {
      channel = supabase
        .channel('user_profile_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_profiles'
          },
          () => {
            loadPermissions();
          }
        )
        .subscribe();
    } catch (error) {
      console.warn('[usePermissions] WebSocket subscription failed (non-critical):', error);
    }

    return () => {
      clearTimeout(timeout);
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  async function loadPermissions() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPermissions(null);
        setUserPlan(null);
        setLoading(false);
        return;
      }

      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('current_plan, selected_metier_module, is_metier, is_pilotage, is_daf, is_associations, is_clients, is_funding')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading permissions:', error);
        setPermissions(null);
        setUserPlan(null);
      } else if (!profile) {
        console.log('No profile found, user needs to complete setup');
        setPermissions(null);
        setUserPlan(null);
      } else {
        const planData: UserPlan = {
          current_plan: profile.current_plan,
          selected_metier_module: profile.selected_metier_module,
          is_metier: profile.is_metier || false,
          is_pilotage: profile.is_pilotage || false,
          is_daf: profile.is_daf || false,
          is_associations: profile.is_associations || false,
          is_clients: profile.is_clients || false,
          is_funding: profile.is_funding || false
        };

        setUserPlan(planData);
        setPermissions(calculatePermissions(planData));
        setNeedsModule(needsModuleSelection(planData));
      }
    } catch (error) {
      console.error('Error in loadPermissions:', error);
      setPermissions(null);
      setUserPlan(null);
    } finally {
      setLoading(false);
    }
  }

  async function updatePlan(newPlan: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_profiles')
        .update({ current_plan: newPlan })
        .eq('id', user.id);

      if (error) throw error;

      await loadPermissions();
    } catch (error) {
      console.error('Error updating plan:', error);
      throw error;
    }
  }

  async function selectModule(moduleSlug: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_profiles')
        .update({ selected_metier_module: moduleSlug })
        .eq('id', user.id);

      if (error) throw error;

      const { data: module } = await supabase
        .from('modules_metiers')
        .select('id')
        .eq('slug', moduleSlug)
        .single();

      if (module) {
        await supabase
          .from('client_modules')
          .delete()
          .eq('user_id', user.id);

        await supabase
          .from('client_modules')
          .insert({
            user_id: user.id,
            module_id: module.id,
            active: true,
            activated_at: new Date().toISOString()
          });
      }

      await loadPermissions();
    } catch (error) {
      console.error('Error selecting module:', error);
      throw error;
    }
  }

  async function clearModule() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_profiles')
        .update({ selected_metier_module: null })
        .eq('id', user.id);

      if (error) throw error;

      await supabase
        .from('client_modules')
        .delete()
        .eq('user_id', user.id);

      await loadPermissions();
    } catch (error) {
      console.error('Error clearing module:', error);
      throw error;
    }
  }

  return {
    permissions,
    userPlan,
    loading,
    needsModuleSelection: needsModule,
    updatePlan,
    selectModule,
    clearModule,
    refresh: loadPermissions
  };
}
